import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Checkbox } from "../../../../ui/components/Checkbox";
import {
  FeatherPlus,
  FeatherX,
  FeatherSave,
  FeatherArrowLeft,
} from "@subframe/core";
import { useToast } from "../../../../shared/presentation/components";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import {
  MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS,
  CHECKLIST_ITEM_PRIORITY_OPTIONS,
  type ChecklistItemPriority,
  type MonthEndChecklistItemWorkflowStatus,
  parseMonthEndChecklistItemWorkflowStatus,
} from "../../../../shared/constants/monthEndChecklist";

const ACCOUNTING_AREAS = MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS;

interface TemplateItem {
  id: string;
  accounting_area: string;
  title: string;
  description: string;
  task_guidance: string[];
  requires_proof: boolean;
  accepted_proof_types: string[];
  display_order: number;
  priority: ChecklistItemPriority;
  link_url?: string;
  link_text?: string;
  status?: MonthEndChecklistItemWorkflowStatus;
}

export const CreateChecklistTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TemplateItem | null>(null);

  const [formData, setFormData] = useState({
    accounting_area: "",
    title: "",
    description: "",
    task_guidance: [""],
    requires_proof: true,
    accepted_proof_types: ["screenshot"],
    display_order: 0,
    priority: "medium" as ChecklistItemPriority,
    link_url: "",
    link_text: "Go to Task",
    status: "not_started" as MonthEndChecklistItemWorkflowStatus,
  });

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      accounting_area: "",
      title: "",
      description: "",
      task_guidance: [""],
      requires_proof: true,
      accepted_proof_types: ["screenshot"],
      display_order: items.length + 1,
      priority: "medium",
      link_url: "",
      link_text: "Go to Task",
      status: "not_started",
    });
    setItemDialogOpen(true);
  };

  const handleEditItem = (item: TemplateItem) => {
    setEditingItem(item);
    setFormData({
      accounting_area: item.accounting_area,
      title: item.title,
      description: item.description,
      task_guidance: item.task_guidance.length > 0 ? item.task_guidance : [""],
      requires_proof: item.requires_proof,
      accepted_proof_types: item.accepted_proof_types,
      display_order: item.display_order,
      priority: item.priority ?? "medium",
      link_url: item.link_url || "",
      link_text: item.link_text || "Go to Task",
      status: parseMonthEndChecklistItemWorkflowStatus(item.status),
    });
    setItemDialogOpen(true);
  };

  const handleDeleteItem = (itemId: string) => {
    setItems(items.filter((item) => item.id !== itemId));
  };

  const handleSaveItem = () => {
    if (!formData.accounting_area || !formData.title || !formData.description?.trim()) {
      showError("Accounting area, title, and description are required");
      return;
    }

    const itemData: TemplateItem = {
      id: editingItem?.id || `item-${Date.now()}`,
      accounting_area: formData.accounting_area,
      title: formData.title,
      description: formData.description,
      task_guidance: formData.task_guidance.filter(
        (step) => step.trim() !== ""
      ),
      requires_proof: formData.requires_proof,
      accepted_proof_types: formData.accepted_proof_types,
      display_order: formData.display_order,
      priority: formData.priority,
      link_url: formData.link_url || undefined,
      link_text: formData.link_text || "Go to Task",
      status: formData.status,
    };

    if (editingItem) {
      setItems(
        items.map((item) => (item.id === editingItem.id ? itemData : item))
      );
    } else {
      setItems([...items, itemData]);
    }

    setItemDialogOpen(false);
    setEditingItem(null);
  };

  const handleAddTaskStep = () => {
    setFormData({
      ...formData,
      task_guidance: [...formData.task_guidance, ""],
    });
  };

  const handleRemoveTaskStep = (index: number) => {
    setFormData({
      ...formData,
      task_guidance: formData.task_guidance.filter((_, i) => i !== index),
    });
  };

  const handleTaskStepChange = (index: number, value: string) => {
    const newSteps = [...formData.task_guidance];
    newSteps[index] = value;
    setFormData({
      ...formData,
      task_guidance: newSteps,
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      showError("Template name is required");
      return;
    }

    if (items.length === 0) {
      showError("Please add at least one template item");
      return;
    }

    try {
      setSaving(true);
      const token = getToken();
      if (!token) {
        showError("Authentication required");
        return;
      }

      // Prepare request body with template + items
      const requestBody = {
        name: templateName,
        description: templateDescription || undefined,
        items: items.map((item) => ({
          accounting_area: item.accounting_area,
          title: item.title,
          description: item.description,
          task_guidance: item.task_guidance,
          accepted_proof_types: item.accepted_proof_types,
          display_order: item.display_order,
          priority: item.priority,
          link_url: item.link_url || undefined,
          link_text: item.link_text || undefined,
          requires_proof: item.requires_proof,
          status: item.status,
        })),
      };

      // Create template with items in one request
      const templateResponse = await fetch(
        `${API_BASE_URL}/api/month-end/template/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!templateResponse.ok) {
        const errorData = await templateResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to create template");
      }

      showSuccess("Template created successfully!");
      navigate("/admin?tab=checklist");
    } catch (error: any) {
      showError(error.message || "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  // Group items by accounting area
  const itemsByArea = items.reduce((acc, item) => {
    if (!acc[item.accounting_area]) {
      acc[item.accounting_area] = [];
    }
    acc[item.accounting_area].push(item);
    return acc;
  }, {} as Record<string, TemplateItem[]>);

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/admin?tab=checklist")}
            />
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Create Month End Checklist Template
              </span>
              <span className="text-body font-body text-subtext-color">
                Create a new template for monthly checklist items
              </span>
            </div>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherSave />}
            onClick={handleSaveTemplate}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </div>

        {/* Template Info */}
        <div className="flex w-full flex-col gap-4 p-6 bg-neutral-50 border border-neutral-border rounded-lg">
          <TextField label="Template Name">
            <TextField.Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
            />
          </TextField>

          <TextArea label="Description">
            <TextArea.Input
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Enter template description (optional)"
            />
          </TextArea>
        </div>

        {/* Items by Accounting Area */}
        <div className="flex w-full flex-col gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Template Items
            </span>
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          </div>

          {Object.keys(itemsByArea).length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 gap-4 border border-neutral-border rounded-lg">
              <span className="text-body font-body text-subtext-color">
                No items added yet. Add your first item to get started.
              </span>
            </div>
          ) : (
            Object.entries(itemsByArea).map(([area, areaItems]) => (
              <div key={area} className="flex flex-col gap-4">
                <h3 className="text-heading-4 font-heading-4 text-default-font">
                  {area}
                </h3>
                <div className="border border-neutral-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-border bg-neutral-50">
                        <th className="px-4 py-3 text-left text-caption-bold font-caption-bold text-default-font">
                          Order
                        </th>
                        <th className="px-4 py-3 text-left text-caption-bold font-caption-bold text-default-font">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-caption-bold font-caption-bold text-default-font">
                          Steps
                        </th>
                        <th className="px-4 py-3 text-right text-caption-bold font-caption-bold text-default-font">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaItems
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((item) => (
                          <tr
                            key={item.id}
                            className="border-b border-neutral-border hover:bg-neutral-50"
                          >
                            <td className="px-4 py-3 text-body font-body text-default-font">
                              {item.display_order}
                            </td>
                            <td className="px-4 py-3 text-body font-body text-default-font">
                              {item.title}
                            </td>
                            <td className="px-4 py-3 text-body font-body text-default-font">
                              {item.task_guidance.length} step(s)
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="neutral-tertiary"
                                  icon={<FeatherPlus />}
                                  onClick={() => handleEditItem(item)}
                                />
                                <Button
                                  variant="neutral-tertiary"
                                  icon={<FeatherX />}
                                  onClick={() => handleDeleteItem(item.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Item Dialog */}
        <DialogLayout
          open={itemDialogOpen}
          onOpenChange={(open) => {
            if (!open && !saving) {
              setItemDialogOpen(false);
              setEditingItem(null);
            }
          }}
          className="max-w-[1200px] w-[90vw]"
        >
          <div className="flex w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {editingItem ? "Edit Template Item" : "Add Template Item"}
              </span>
              <Button
                variant="neutral-tertiary"
                icon={<FeatherX />}
                onClick={() => {
                  if (!saving) {
                    setItemDialogOpen(false);
                    setEditingItem(null);
                  }
                }}
              />
            </div>
            <div className="flex flex-col gap-4 w-full">
              <Select
                label="Accounting Area"
                value={formData.accounting_area}
                onValueChange={(value: string) =>
                  setFormData({ ...formData, accounting_area: value || "" })
                }
                placeholder="Select Accounting Area"
              >
                {ACCOUNTING_AREAS.map((area) => (
                  <Select.Item key={area.value} value={area.value}>
                    {area.label}
                  </Select.Item>
                ))}
              </Select>

              <Select
                label="Priority"
                value={formData.priority}
                onValueChange={(value: string) => {
                  const v = value as ChecklistItemPriority;
                  if (v === "high" || v === "medium" || v === "low") {
                    setFormData({ ...formData, priority: v });
                  }
                }}
                placeholder="Select priority"
              >
                {CHECKLIST_ITEM_PRIORITY_OPTIONS.map((p) => (
                  <Select.Item key={p.value} value={p.value}>
                    {p.label}
                  </Select.Item>
                ))}
              </Select>

              <TextField label="Title">
                <TextField.Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Enter item title"
                />
              </TextField>

              <TextArea label="Description *">
                <TextArea.Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter item description"
                />
              </TextArea>

              <div className="flex flex-col gap-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Task Guidance (Steps)
                </span>
                {formData.task_guidance.map((step, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <TextField className="flex-1">
                      <TextField.Input
                        value={step}
                        onChange={(e) =>
                          handleTaskStepChange(index, e.target.value)
                        }
                        placeholder={`Step ${index + 1}`}
                      />
                    </TextField>
                    {formData.task_guidance.length > 1 && (
                      <Button
                        variant="neutral-tertiary"
                        icon={<FeatherX />}
                        onClick={() => handleRemoveTaskStep(index)}
                      />
                    )}
                  </div>
                ))}
                <Button
                  variant="neutral-secondary"
                  onClick={handleAddTaskStep}
                  icon={<FeatherPlus />}
                >
                  Add Step
                </Button>
              </div>

              <TextField label="Display Order">
                <TextField.Input
                  type="number"
                  value={formData.display_order.toString()}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      display_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </TextField>

              <Checkbox
                checked={formData.requires_proof}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    requires_proof: Boolean(checked),
                  })
                }
                label="Requires proof before marking done (screenshot / attachment)"
              />

              <Checkbox
                checked={formData.status === "to_be_done_later"}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    status: checked
                      ? "to_be_done_later"
                      : "not_started",
                  })
                }
                label="Mark as to be done later (default status for new month items from this line)"
              />

              <div className="flex items-center justify-end gap-2 pt-4">
                <Button
                  variant="neutral-secondary"
                  onClick={() => {
                    if (!saving) {
                      setItemDialogOpen(false);
                      setEditingItem(null);
                    }
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleSaveItem}
                  disabled={saving}
                >
                  {editingItem ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </div>
        </DialogLayout>
      </div>
    </DefaultPageLayout>
  );
};
